/**
 * WebAPI Store
 * Manages CDM sources and generation jobs
 */
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useSourceAccessFor } from '@/composables/useEntityAccess'
import type { CDMSource, GenerationJob, CohortGenerationInfoList } from '@/models/webapi.types'
import { fetchCDMSources } from '@/services/source.service'
import { generateCohort as generateCohortRequest, getCohortGenerationInfo } from '@/services/cohort-definition.service'
import { useExecutionPolling } from '@/composables/useExecutionPolling'
import { logger } from '@/utils/logger'
import { useI18n } from '@/composables/useI18n'

const SELECTION_STORAGE_KEYS = ['selectedVocabulary', 'selectedEvidence', 'selectedResults']

export const useWebAPIStore = defineStore('webapi', () => {
  const { tv } = useI18n()
  // State
  const sources = ref<CDMSource[]>([])
  const selectedSource = ref<string | null>(null)
  const generationJobs = ref<Map<number, GenerationJob>>(new Map())
  const isLoadingSources = ref(false)

  const authStore = useAuthStore()
  const sourceAccess = useSourceAccessFor()
  // admin:source can list sources it cannot query, so selection must only consider readable ones.
  const isReadable = (s: CDMSource) => sourceAccess.canRead(s.sourceId)

  // Getters
  const sourcesList = computed(() => sources.value)

  const currentSource = computed(() => {
    if (!selectedSource.value) return null
    return sources.value.find(s => s.sourceKey === selectedSource.value) ?? null
  })

  const activeJobs = computed(() => {
    return Array.from(generationJobs.value.values()).filter(
      job => job.status === 'PENDING' || job.status === 'RUNNING'
    )
  })

  function getJobById(id: number): GenerationJob | undefined {
    return generationJobs.value.get(id)
  }

  function getJobsByCohortId(cohortId: number): GenerationJob[] {
    return Array.from(generationJobs.value.values()).filter(
      job => job.cohortDefinitionId === cohortId
    )
  }

  const vocabularySources = computed(() => {
    return sources.value.filter(
      s => isReadable(s) && s.daimons?.some(d => d.daimonType === 'Vocabulary')
    )
  })

  /**
   * Sources that can report record and person counts. A vocabulary source
   * knows the concepts; only a source with a Results daimon knows how often
   * they occur in that data.
   */
  const resultsSources = computed(() => {
    return sources.value.filter(
      s => isReadable(s) && s.daimons?.some(d => d.daimonType === 'Results')
    )
  })

  /** Returns validated vocabulary source key, auto-correcting invalid localStorage values */
  function getValidVocabularySource(): string | null {
    const storedVocab = localStorage.getItem('selectedVocabulary')

    if (
      storedVocab &&
      storedVocab.trim() !== '' &&
      storedVocab !== 'null' &&
      storedVocab !== 'undefined'
    ) {
      if (vocabularySources.value.some(s => s.sourceKey === storedVocab)) {
        return storedVocab
      }
      logger.warn('WebAPIStore', 'Stored vocabulary source not found', { stored: storedVocab })
    }

    const firstVocabSource = priorityDefault('Vocabulary')
    if (firstVocabSource) {
      localStorage.setItem('selectedVocabulary', firstVocabSource.sourceKey)
      return firstVocabSource.sourceKey
    }

    return null
  }

  /** Highest daimon priority of the given type wins; list order breaks ties. */
  function priorityDefault(daimonType: string): CDMSource | undefined {
    const priorityOf = (s: CDMSource) =>
      Math.max(...s.daimons.filter(d => d.daimonType === daimonType).map(d => d.priority ?? 0))
    return sources.value
      .filter(s => isReadable(s) && s.daimons?.some(d => d.daimonType === daimonType))
      .reduce<CDMSource | undefined>(
        (best, s) => (!best || priorityOf(s) > priorityOf(best) ? s : best),
        undefined
      )
  }

  /** Drops stored selections the current user can no longer see so defaults apply again. */
  function reconcileStoredSelections(): void {
    const stored: Array<[string, string]> = [
      ['selectedVocabulary', 'Vocabulary'],
      ['selectedEvidence', 'CEM'],
      ['selectedResults', 'Results'],
    ]
    for (const [storageKey, daimonType] of stored) {
      const value = localStorage.getItem(storageKey)
      if (!value) continue
      const visible = sources.value.some(
        s =>
          s.sourceKey === value &&
          isReadable(s) &&
          s.daimons?.some(d => d.daimonType === daimonType)
      )
      if (!visible) {
        logger.info('WebAPIStore', 'Stored source no longer available, resetting', {
          storageKey,
          value,
        })
        localStorage.removeItem(storageKey)
      }
    }
  }

  // Actions
  function setSources(sourcesList: CDMSource[]) {
    sources.value = sourcesList
    // An empty list is usually a failed fetch; keep the user's choices.
    if (sourcesList.length === 0) return
    reconcileStoredSelections()
    const stillVisible = sourcesList.some(s => s.sourceKey === selectedSource.value && isReadable(s))
    if (!stillVisible) {
      selectedSource.value =
        priorityDefault('Results')?.sourceKey ?? sourcesList.find(isReadable)?.sourceKey ?? null
      logger.debug('WebAPIStore', 'Auto-selected default source', {
        selected: selectedSource.value,
        sources: sourcesList.map(s => s.sourceKey),
      })
    }
  }

  function setSelectedSource(sourceKey: string) {
    selectedSource.value = sourceKey
  }

  function setLoadingSources(loading: boolean) {
    isLoadingSources.value = loading
  }

  function addGenerationJob(job: GenerationJob) {
    generationJobs.value.set(job.id, job)
  }

  function updateGenerationJob(id: number, job: GenerationJob) {
    generationJobs.value.set(id, job)
  }

  function removeGenerationJob(id: number) {
    generationJobs.value.delete(id)
  }

  function clearJobs() {
    generationJobs.value.clear()
  }

  // Generation workflow actions

  /**
   * Fetch available CDM data sources
   */
  async function fetchSources(): Promise<void> {
    try {
      setLoadingSources(true)
      const result = await fetchCDMSources()
      if (result.success) {
        setSources(result.data)
      } else {
        logger.error('WebAPIStore', 'Failed to fetch CDM sources', result.error)
        setSources([])
      }
    } catch (error) {
      logger.error('WebAPIStore', 'Unexpected error fetching CDM sources', error)
      setSources([])
    } finally {
      setLoadingSources(false)
    }
  }

  /** True when any tracked selection (stored or in-memory) is a known source the user can no longer read. */
  function hasLostSelectedSourceAccess(): boolean {
    const keys = [
      selectedSource.value,
      ...SELECTION_STORAGE_KEYS.map(k => localStorage.getItem(k)),
    ]
    return keys.some(key => {
      const source = key ? sources.value.find(s => s.sourceKey === key) : undefined
      return !!source && !isReadable(source)
    })
  }

  // The user/me poll replaces permissions and entityAccess each cycle, so this runs
  // every refresh but only fetches when a selected source lost read access or the user changed.
  watch(
    [() => authStore.user?.login ?? null, () => authStore.permissions, () => authStore.entityAccess],
    ([login], [previousLogin]) => {
      if (login !== previousLogin || hasLostSelectedSourceAccess()) void fetchSources()
    }
  )

  /**
   * Generate cohort for a specific data source.
   *
   * Jobs are keyed by sourceId so they line up with the /info endpoint, which
   * returns entries keyed by `id.sourceId`. When sources aren't loaded we
   * fall back to an existing job slot or the API-supplied id so polling can
   * still match the entry by `info.id.sourceId === job.id`.
   */
  async function generateCohort(
    cohortId: number,
    sourceKey: string
  ): Promise<GenerationJob | null> {
    const source = sources.value.find(s => s.sourceKey === sourceKey)
    const existingJob = getJobsByCohortId(cohortId).find(j => j.sourceKey === sourceKey)
    const knownKey = source?.sourceId ?? existingJob?.id

    if (knownKey != null) {
      // Optimistically show PENDING while the start request is in flight.
      updateGenerationJob(knownKey, {
        id: knownKey,
        cohortDefinitionId: cohortId,
        sourceKey,
        status: 'PENDING',
        startTime: existingJob?.startTime ?? new Date().toISOString(),
      })
    }

    try {
      const result = await generateCohortRequest(cohortId, sourceKey)

      if (result.success) {
        const job = result.data
        const finalKey = knownKey ?? job.id
        if (knownKey != null && knownKey !== finalKey) {
          removeGenerationJob(knownKey)
        }
        updateGenerationJob(finalKey, {
          ...job,
          id: finalKey,
          cohortDefinitionId: cohortId,
          sourceKey,
        })
        pollGenerationStatus(cohortId)
        return job
      }

      // API call resolved but failed — surface as failure so the UI
      // doesn't sit on PENDING forever.
      if (knownKey != null) {
        updateGenerationJob(knownKey, {
          id: knownKey,
          cohortDefinitionId: cohortId,
          sourceKey,
          status: 'FAILED',
          failMessage: result.error.message,
        })
      }
      logger.error('WebAPIStore', 'Failed to generate cohort', result.error)
      return null
    } catch (error) {
      if (knownKey != null) {
        updateGenerationJob(knownKey, {
          id: knownKey,
          cohortDefinitionId: cohortId,
          sourceKey,
          status: 'FAILED',
          failMessage: error instanceof Error ? error.message : tv('cohortDefinitions.generation.failed'),
        })
      }
      logger.error('WebAPIStore', 'Failed to generate cohort', error)
      return null
    }
  }

  /**
   * Poll generation status every 2 seconds until complete or failed
   */
  const POLL_INTERVAL_MS = 2000 // 2 seconds
  const POLL_TIMEOUT_MS = 300000 // 5 minutes max
  const pollingStops = new Map<number, () => void>()

  function applyGenerationInfo(cohortId: number, infoList: CohortGenerationInfoList): void {
    for (const info of infoList) {
      // Jobs are keyed by sourceId (matches /info entries directly).
      const existing = generationJobs.value.get(info.id.sourceId)
      // Only update jobs we actually started for this cohort. /info may
      // return entries we don't have a job for, leave those alone.
      if (!existing || existing.cohortDefinitionId !== cohortId) continue

      const source = sources.value.find(s => s.sourceId === info.id.sourceId)
      const sourceKey = source?.sourceKey ?? existing.sourceKey

      updateGenerationJob(info.id.sourceId, {
        id: info.id.sourceId,
        cohortDefinitionId: cohortId,
        sourceKey,
        status: info.status,
        personCount: info.personCount ?? undefined,
        recordCount: info.recordCount ?? undefined,
        startTime: info.startTime ? new Date(info.startTime).toISOString() : existing.startTime,
        endTime:
          info.status === 'COMPLETE' || info.status === 'FAILED'
            ? info.startTime != null && info.executionDuration != null
              ? new Date(info.startTime + info.executionDuration).toISOString()
              : new Date().toISOString()
            : undefined,
        failMessage: info.failMessage ?? undefined,
      })
    }
  }

  async function pollGenerationStatus(cohortId: number): Promise<void> {
    stopPolling(cohortId)

    const startTime = Date.now()

    const polling = useExecutionPolling<{ done: boolean }>({
      intervalMs: POLL_INTERVAL_MS,
      fetcher: async () => {
        const result = await getCohortGenerationInfo(cohortId)

        // An empty/failed response doesn't mean we're done: a freshly kicked
        // off generation may not be indexed yet. Keep polling and rely on the
        // per-cohort terminal-state check to stop.
        if (result.success) {
          applyGenerationInfo(cohortId, result.data)
        }

        const stillActive = getJobsByCohortId(cohortId).some(
          j => j.status === 'PENDING' || j.status === 'RUNNING'
        )

        if (stillActive && Date.now() - startTime > POLL_TIMEOUT_MS) {
          logger.warn('WebAPIStore', `Generation polling timeout for cohort ${cohortId}`)
          return { done: true }
        }

        return { done: !stillActive }
      },
      isTerminal: item => item.done,
      onUpdate: item => {
        if (item.done) {
          pollingStops.delete(cohortId)
        }
      },
    })

    pollingStops.set(cohortId, polling.stop)

    await polling.start()
  }

  /**
   * Stop polling for a specific cohort
   */
  function stopPolling(cohortId: number): void {
    const stop = pollingStops.get(cohortId)
    if (stop) {
      stop()
      pollingStops.delete(cohortId)
    }
  }

  /**
   * Stop all active polling
   */
  function stopAllPolling(): void {
    pollingStops.forEach(stop => stop())
    pollingStops.clear()
  }

  /**
   * Fetch existing generation info for a cohort and convert to GenerationJob format
   * This loads previously generated cohort data from the backend
   */
  async function fetchCohortGenerationInfo(cohortId: number): Promise<void> {
    try {
      const result = await getCohortGenerationInfo(cohortId)

      if (!result.success || result.data.length === 0) {
        return
      }

      const infoList = result.data

      // Convert each generation info to a GenerationJob
      // We need to map sourceId to sourceKey
      for (const info of infoList) {
        // Find the source that matches this sourceId
        const source = sources.value.find(s => s.sourceId === info.id.sourceId)
        if (!source) {
          logger.warn('WebAPIStore', `Could not find source with ID ${info.id.sourceId}`)
          continue
        }

        // Create a GenerationJob from the info
        const job: GenerationJob = {
          id: info.id.sourceId, // Use sourceId as job ID (since we don't have a separate job ID)
          cohortDefinitionId: info.id.cohortDefinitionId,
          sourceKey: source.sourceKey,
          status: info.status,
          startTime: info.startTime ? new Date(info.startTime).toISOString() : undefined,
          endTime:
            info.executionDuration && info.startTime
              ? new Date(info.startTime + info.executionDuration).toISOString()
              : undefined,
          personCount: info.personCount ?? undefined,
          recordCount: info.recordCount ?? undefined,
          failMessage: info.failMessage ?? undefined,
        }

        addGenerationJob(job)
      }
    } catch (error) {
      logger.error('WebAPIStore', 'Failed to fetch cohort generation info', error)
    }
  }

  // Cleanup function
  function dispose() {
    stopAllPolling()
  }

  return {
    // State
    sources,
    selectedSource,
    generationJobs,
    isLoadingSources,
    // Getters
    sourcesList,
    resultsSources,
    currentSource,
    activeJobs,
    vocabularySources,
    getJobById,
    getJobsByCohortId,
    getValidVocabularySource,
    // Actions
    setSources,
    setSelectedSource,
    setLoadingSources,
    addGenerationJob,
    updateGenerationJob,
    removeGenerationJob,
    clearJobs,
    // Generation workflow
    fetchSources,
    fetchCohortGenerationInfo,
    generateCohort,
    pollGenerationStatus,
    stopPolling,
    stopAllPolling,
    // Cleanup
    dispose,
    // Constants (for testing)
    POLL_INTERVAL_MS,
    POLL_TIMEOUT_MS,
  }
})
